import { memo } from 'react';

import { Copy } from 'lucide-react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';

import { notify } from '@/components/toast';
import type { ColumnWidth } from '@/components/ui/table/columnWidth';
import { getTextData } from '@/components/ui/widget/utils/widget.utils';
import type { TextView } from '@/components/ui/widget/views/text/text.view.type';
import type { WidgetData } from '../../widget.type';
import type { IdView } from './id.view.type';

type Props = {
  view: IdView;
  data: WidgetData;
};

// simple text to display
const IdViewImpl = memo(({ view, data }: Props) => {
  const { path, default: defaultValue } = view;

  const textData = getTextData(data, path, defaultValue);

  const handleCopy = async () => {
    if (!textData) {
      notify.error('No ID to copy');
      return;
    }
    try {
      await navigator.clipboard.writeText(textData);
      notify.success('ID copied to clipboard');
    } catch (error) {
      console.error('Failed to copy to clipboard', error);
      notify.error('Cannot copy to clipboard in http mode.');
    }
  };

  return (
    <div className="flex w-full justify-center" onClick={handleCopy}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="w-fit max-w-9/10 cursor-pointer truncate rounded-full border border-primary-deep/10 bg-primary-deep/5 px-3 py-1 font-medium font-mono text-primary-deep/60 text-xs tracking-normal hover:border-primary-deep/40 dark:border-primary/30 dark:bg-primary/10 dark:text-primary hover:dark:border-primary">
              {textData}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <div className="flex items-center gap-2">
              <Copy size={14} />
              Click to copy the ID
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
});

// sort defaults to text on path
const getSortConfig = (view: IdView): TextView => ({
  type: 'text',
  path: view.path
});

// filtering defaults to text search on path
const getFilterConfig = (view: IdView): TextView => ({
  type: 'text',
  path: view.path
});

// width defaults to 'medium'
const getDefaultWidth = (): ColumnWidth => 'medium';

// stringifies data at path
const getStringifyFunction = (view: IdView) => {
  const { path, default: defaultValue } = view;
  return (data: WidgetData) => getTextData(data, path, defaultValue);
};

export const idViewProvider = {
  component: IdViewImpl,
  getSortConfig,
  getFilterConfig,
  getDefaultWidth,
  getStringifyFunction
};
